import type { UserLocalStorage } from '../../types/localStorage';

import { useCallback, useMemo } from 'react';
import useLocalStorageState from 'use-local-storage-state';

import { getEnv } from '../../lib/env';
import { putUserConfig } from '../../lib/flotiq-client';
import { getNotifications } from '../../lib/flotiq-client/external-services-requests';

import useToken from '../useToken';
import { createListingHook } from './hook-builders/listing-hook-builder';

const useNotificationsBaseHook = createListingHook(
  getNotifications,
  'notifications',
);

const NOTIFICATIONS_PARAMS = {
  limit: 3,
  order_by: 'internal.createdAt',
  order_direction: 'desc',
};

export const useNotifications = () => {
  const [user, setUser] = useLocalStorageState<UserLocalStorage>('cms.user');
  const jwt = useToken();

  const notificationsEnabled =
    !!getEnv('VITE_NOTIFICATIONS_URL') &&
    !!getEnv('VITE_NOTIFICATIONS_X_AUTH_TOKEN');

  const hookOptions = useMemo(
    () => ({
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchInterval: 5 * 60 * 1000, // 5 minutes
      refetchIntervalInBackground: true,
      pause: !notificationsEnabled,
    }),
    [notificationsEnabled],
  );

  const { data: notifications, isLoading: notificationsLoading } =
    useNotificationsBaseHook(NOTIFICATIONS_PARAMS, hookOptions);

  const onNotificationsRead = useCallback(
    (lastNotificationDate: string) => {
      if (
        !user?.data?.id ||
        user.data?.config?.lastNotificationDate === lastNotificationDate
      ) {
        return;
      }

      if (user?.data?.id) {
        const newConfig = { ...(user.data.config || {}) };
        newConfig.lastNotificationDate = lastNotificationDate;

        putUserConfig(jwt, undefined, {
          id: user.data.id,
          config: newConfig,
        }).catch(() => {
          //do nothing and update config for current session
        });

        const newUser = { ...user };
        newUser.data.config = newConfig;
        setUser(newUser);
      }
    },
    [jwt, setUser, user],
  );

  return {
    notifications,
    notificationsLoading,
    onNotificationsRead,
  };
};
